import { JSX, useContext, useEffect, useMemo, useState } from "react";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "./waffles";
import { AdvancedExclusionsContext } from "@app/projects/projection-provider/advanced-exclusions/AdvancedExclusionsContext";
import { Waffles } from "./waffles/Waffles";
import { SharedBulkSelectProject } from "@app/projects/projection-provider/advanced-exclusions/sharedBulkSelectProject";

interface BulkProjectSelectLOBTableProps {
  appendCurrentProjectToList: (projectList: string[]) => void;
  searchString?: string;
  selections: string[];
  setSelections: (selections: string[]) => void;
};

interface LOBTableData {
  [wafflesKeyColumnName]: string;
  lob: string;
  projects: SharedBulkSelectProject[];
  numberOfProjects: number;
}

/* oxlint-disable sort-keys */
const LOBColumnConfig: ColumnConfig = {
  lob: {
    displayText: "Line of Business",
    testId: "line-of-business",
  },
  numberOfProjects: {
    displayText: "Number of Projects",
    testId: "number-of-projects"
  },
}
/* oxlint-enable sort-keys */

const customSortLOB = (rows: LOBTableData[], key: keyof LOBTableData, direction: SortDirections) => {
  const directionMultiplier = direction === SortDirections.Asc ? 1 : -1;
  return rows.sort((a, b) => (a[key] > b[key] ? 1 : -1) * directionMultiplier);
}
interface LOBLookup {
  [lobName: string]: SharedBulkSelectProject[];
}

const BulkProjectSelectLOBTable = (props: BulkProjectSelectLOBTableProps): JSX.Element => {
  
  const [originLOBData, setOriginLOBData] = useState<LOBTableData[]>();
  const [selectedLOBs, setSelectedLOBs] = useState<string[]>(props.selections);
  const [LOBLookup, setLOBLookup] = useState<LOBLookup>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const wizardContext = useContext(AdvancedExclusionsContext);
  const currentProjectExists = wizardContext.currentProjectId != null && wizardContext.currentProjectId !== "";

  useEffect(() => {
    setIsLoading(true);
    const newLOBLookup: LOBLookup = {};
    const allLOBs = [...new Set<string>(wizardContext.projects.flatMap((p) => p.lob))].filter((lob) => lob !== undefined);
    allLOBs.forEach((t) => {
      const projects: SharedBulkSelectProject[] = [];
      wizardContext.projects.forEach((p) => {
        if (p.lob?.includes(t) && !p.rowIsDisabled && (!currentProjectExists || p.projectId.toString() != wizardContext.currentProjectId)) {
          projects.push(p);
        }
      })
      newLOBLookup[t] = projects;
    });
    setIsLoading(false);
    setLOBLookup(newLOBLookup);
  }, [wizardContext.projects]);

  /* oxlint-disable sort-keys */
  const lobTableData: LOBTableData[] = useMemo(() => {
    if (!!props.searchString) {
      const escapedSearchString = props.searchString.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
      const regex = new RegExp(escapedSearchString, "i");
      const filteredLOBData = originLOBData?.filter(lob => !!lob?.lob?.match(regex));

      return filteredLOBData?.map<LOBTableData>((lob) => {

        return ({
          [wafflesKeyColumnName]: lob.lob.replaceAll(" ", "-"),
          lob: lob.lob,
          projects: lob.projects,
          numberOfProjects: lob.numberOfProjects,
        })
      })
    }

    let lobsWithProjects: LOBTableData[] = [];
    const currentLOBList = Object.keys(LOBLookup);

    if (currentLOBList?.length > 0) {
      currentLOBList?.forEach(lob => {

        lobsWithProjects.push({
          lob: lob,
          projects: [],
          numberOfProjects: 0,
          [wafflesKeyColumnName]: lob.replaceAll(" ", "-"),
        })
      });

      wizardContext.projects?.filter(project => !project.rowIsDisabled && (!currentProjectExists || project.projectId.toString() != wizardContext.currentProjectId))?.forEach(project => {
        lobsWithProjects.forEach(lobWithProjects => {
          if (project.lob == lobWithProjects.lob) {
            lobWithProjects.projects.push(project);
          }
        });
      });
    }

    lobsWithProjects.forEach(lobWithProjects => {
      if (lobWithProjects.projects.length <= 0) {
        lobsWithProjects = lobsWithProjects.filter(x => x.lob != lobWithProjects.lob);
      }
      else {
        lobWithProjects.numberOfProjects = lobWithProjects.projects.length;
      }
    });


    setOriginLOBData(lobsWithProjects);
    return lobsWithProjects;

  }, [wizardContext.projects, LOBLookup, props.searchString]);
  /* oxlint-enable sort-keys */

  const handleCheckboxChange = (selectedProjectIds: string[]) => {
    setSelectedLOBs(selectedProjectIds);
    props.setSelections(selectedProjectIds);

    const selectedLOBs: string[] = [];
    selectedProjectIds.forEach(selection => {
      if (!selectedLOBs.includes(selection.split(":")[0].replaceAll('-', ' '))) {
        selectedLOBs.push(selection.split(":")[0].replaceAll('-', ' '));
      }
    });

    const selectedProjectIdsList: string[] = [];
    lobTableData.forEach(element => {

      if (selectedLOBs.includes(element.lob)) {
        selectedProjectIdsList.push(
          ...selectedProjectIdsList,
          ...element.projects
            .filter(x => x.rowIsDisabled === false)
            .map(x => x.projectId.toString())
        );
      }
    });

    const uniqeProjectIdsList: string[] = [...new Set(selectedProjectIdsList)];
    props.appendCurrentProjectToList(uniqeProjectIdsList);
    wizardContext.updateSelectedProjects(uniqeProjectIdsList);
  }

  return (
    <Waffles<LOBTableData>
      columns={LOBColumnConfig}
      customSort={customSortLOB}
      data={lobTableData}
      disableProcessingOverlay={true}
      fullWidth={true}
      loading={isLoading}
      handleCheckboxChange={handleCheckboxChange}
      selectedKeys={selectedLOBs}
      showCheckboxes={true}
      testId="volume-reports-projects-table"
    />
  );
};

export default BulkProjectSelectLOBTable;